import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { setDashboardTab } from '../store/slices/globalSlice';
import BookingsFill from '../components/icons/BookingsFill';
import LoadingPage from './common/LoadingPage';
import { DashboardDateRange, DashboardDateRangeType } from '../types/bookingCreation';
import { useGetDashboardData } from '../flows/dashboardDataFlow';
import { formatCurrency, getDashboardDateRange } from '../utils';
import { RootState } from '../store/store';
import dayjs, { Dayjs } from 'dayjs';
import { DateRangePicker } from '../components/DateRangePicker';
import PageHeader from '../components/PageHeader';
import PageHeadline from '../components/PageHeadline';
import { makeHasPermissionSelector, tenantSelector } from '../selector/authenticationSelector';
import Permission from '../common/permissions';
import { useIsMaster } from '../common/useIsMaster';
import { isSelfServiceTenant } from '../common/tenantStore';
import {
    makeBookingsAwaitingApprovalUrl,
    makeIssueReportDetailsUrl,
    makeRentalBookingDetailsUrl,
    makeRentalIssueReportDetailsUrl,
    paths,
} from '../routes';
import { useGetAssetsAvailabilityDataQuery } from '../api/booking/bookingApiSlice';
import { getCreateBookingOptionsForTenant } from '../config/bookingServiceMapping';
import DropdownMenu from '../components/DropdownMenu';
import { RecentBookingItem, RecentIssueReportItem } from '../types/bookingCreation';
import {
    AlertTriangle, ArrowRight, BarChart3, Building2, Check, ChevronDown, CreditCard, Euro,
    Globe, Hourglass, Info, Mail, MapPin, Plus, Rocket, Scale, SlidersHorizontal, TrendingDown, TrendingUp,
} from 'lucide-react';
import * as Select from '@radix-ui/react-select';
import { Tooltip } from '@mui/material';
import { cn } from '../utils/utils';
import { useCanAccessAccount } from '../hooks/useCanAccessAccount';
import { useSetupProgress } from '../hooks/useSetupProgress';
import { enqueueWarningSnackbar } from '../action/snackbarCommands';
import { STATION_TIME_ZONE } from '../utils/stationTime';
import { opensSingleObjectCreate, roomUseCasesOf } from '../utils/roomUseCases';
import { failedDashboardMetrics } from './dashboard/dashboardFailure';
import DaySummarySection from './dashboard/DaySummarySection';

interface TabsPros {
    key: DashboardDateRangeType;
    label: string;
}

// `formatCurrency` drops the sign (it formats Math.abs). Net revenue can
// legitimately be negative now that refunds and dispute losses come off (#2028)
// — a period whose refunds exceed its accrued charges — and rendering -60 € as
// 60 € would be worse than showing nothing.
const formatSignedCurrency = (value: number | string | undefined, locale: string): string => {
    const numeric = Number(value ?? 0);
    const safe = Number.isFinite(numeric) ? numeric : 0;
    return `${safe < 0 ? '-' : ''}${formatCurrency(safe, locale)}`;
};

// Which create flow a tenant's objects go through. The room family (the space
// template) is single listings and keeps `AssetCreatePage` (Flow 1); everything else is a station
// with objects and goes through the creation wizard (Flow 2,
// `docs/plans/object-and-station-creation-flow.md` §3).
//
// There is no third case any more. This set used to gate a disabled "Demnächst
// verfügbar" tile for storage/sup/keys — telling a tenant to wait for something
// that had already shipped, and which the sidebar's own create menu offered two
// clicks away.

// An activity card emptied by a failed request says so, rather than "nothing
// to show" (#2864).
const activityEmptyKey = (requestFailed: boolean, emptyKey: string): string =>
    requestFailed ? 'dashboard.figure_failed' : emptyKey;

/** Where "Objekt erstellen" goes for this tenant's services. */
const createObjectRouteFor = (services: string[]): string =>
    opensSingleObjectCreate(services)
        ? paths.createAsset
        : paths.createStation;

export default function Dashboard() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const lang = i18n.language;
    const TABS: TabsPros[] = [
        { key: 'last7days', label: t('dashboard.last_7_days') },
        { key: 'currentMonth', label: t('dashboard.current_month') },
        { key: 'lastMonth', label: t('dashboard.last_month') },
        { key: 'yearToDate', label: t('dashboard.year_to_date') },
        { key: 'customRange', label: t('dashboard.custom_range') },
    ];
    const dispatch = useDispatch();
    const { isLoading, getDashboardData } = useGetDashboardData();
    const tenant = useSelector(tenantSelector);
    const {
        dashboardTab,
        dashboardData: {
            stats,
            recentBookings,
            recentIssueReports,
            awaitingApproval,
        },
        dashboardDataFailed,
    } = useSelector((state: RootState) => state.global);
    const { bookings, revenues, utilization } = stats;
    // #2864: a tile whose metric failed says so — it never falls back to 0.
    const failedMetrics = failedDashboardMetrics(stats, dashboardDataFailed);
    // Defensive defaults: tolerate a brief deploy gap where the backend
    // hasn't shipped #878 yet and these fields are missing from the response.
    const safeRecentBookings = recentBookings ?? [];
    const safeRecentIssueReports = recentIssueReports ?? [];
    const revenueCaptureRate = utilization?.revenueCaptureRate;
    const perService = utilization?.perService ?? [];
    // #2144: rooms contracted out under a space agreement. They are not in
    // `perService` — they left the capture rate on both sides — so the tile
    // has to say so, or capacity silently disappears from the dashboard.
    // `?? []` is the older-deployment case: absent means nothing contracted.
    const contractedRooms = utilization?.contracted ?? [];
    const bookingsPerService = bookings?.perService ?? [];
    const revenuesPerService = revenues?.perService ?? [];

    // Service selector — global filter applied to all KPI tiles. Backend now
    // splits per service for all three KPIs (utilization since #877, bookings
    // and revenues since #895), so filtered tiles render the aggregated value
    // for the selected service.
    const [selectedService, setSelectedService] = React.useState<string>('all');
    const serviceTypes = useMemo(() => {
        const set = new Set<string>();
        for (const s of perService) set.add(s.serviceType);
        for (const s of bookingsPerService) set.add(s.serviceType);
        for (const s of revenuesPerService) set.add(s.serviceType);
        return Array.from(set);
    }, [perService, bookingsPerService, revenuesPerService]);
    // Reset filter if the active selection disappears (e.g. after switching tenant
    // or date range with no data for that service).
    useEffect(() => {
        if (selectedService !== 'all' && !serviceTypes.includes(selectedService)) {
            setSelectedService('all');
        }
    }, [serviceTypes, selectedService]);

    // Filtered capture rate: weighted-average utilization for the selected
    // serviceType (multiple groups can map to one service). Trend is omitted
    // because per-service previous-period booked hours aren't on the type;
    // hiding the chip avoids approximations on the dashboard top-line.
    // Rendered as the backend reports it, unclamped (#2864 D5): booked and
    // bookable hours are measured over the same time and the same objects
    // there, so a row cannot exceed 100 % — and if one ever did, a clamp here
    // would hide exactly the defect that needs reporting.
    const filteredCaptureRate = useMemo(() => {
        if (selectedService === 'all') return null;
        const matching = perService.filter((s) => s.serviceType === selectedService);
        const totalBookable = matching.reduce((acc, s) => acc + s.bookableHours, 0);
        const totalBooked = matching.reduce((acc, s) => acc + s.bookedHours, 0);
        // No on-demand capacity is NOT 0 % utilization. It is the state of a
        // service whose rooms are all contracted out (#2144) or all blocked —
        // and rendering it as 0 % reads as "nobody booked anything", which is
        // the opposite of what happened. `null` surfaces as n/a.
        if (totalBookable <= 0) return null;
        return (totalBooked / totalBookable) * 100;
    }, [selectedService, perService]);

    // Contracted hours for the current filter, as a plain figure under the
    // rate. Not part of it — that is the whole point of the amendment.
    const contractedHoursShown = useMemo(() => {
        const matching = selectedService === 'all'
            ? contractedRooms
            : contractedRooms.filter((room) => room.serviceType === selectedService);
        return matching.reduce((acc, room) => acc + room.contractedHours, 0);
    }, [selectedService, contractedRooms]);

    // Filtered bookings/revenues (#895): sum each group's TrendStats.current
    // across the groups mapped to the selected serviceType. Returns null in
    // 'all' mode so callers fall back to the top-line. Trend chip stays
    // hidden under filter — same rationale as filteredCaptureRate.
    const filteredBookings = useMemo(() => {
        if (selectedService === 'all') return null;
        const matching = bookingsPerService.filter((s) => s.serviceType === selectedService);
        return matching.reduce((acc, s) => acc + Number(s.current.current), 0);
    }, [selectedService, bookingsPerService]);

    const filteredRevenues = useMemo(() => {
        if (selectedService === 'all') return null;
        const matching = revenuesPerService.filter((s) => s.serviceType === selectedService);
        return matching.reduce((acc, s) => acc + Number(s.current.current), 0);
    }, [selectedService, revenuesPerService]);

    // Gross / refunds / disputes behind the net figure (#2028). Reported as
    // separate numbers rather than one blended total — a period where we took
    // 100 and refunded 40 is a different fact from one where we took 60.
    // Undefined while the backend slice hasn't deployed yet; the tile then
    // renders the headline alone, exactly as before.
    const revenueBreakdown = useMemo(() => {
        if (selectedService === 'all') {
            if (revenues?.gross === undefined) return null;
            return {
                gross: Number(revenues.gross),
                refunds: Number(revenues.refunds ?? 0),
                disputes: Number(revenues.disputes ?? 0),
            };
        }
        const matching = revenuesPerService.filter((s) => s.serviceType === selectedService);
        if (!matching.length || matching[0].gross === undefined) return null;
        return matching.reduce(
            (acc, s) => ({
                gross: acc.gross + Number(s.gross ?? 0),
                refunds: acc.refunds + Number(s.refunds ?? 0),
                disputes: acc.disputes + Number(s.disputes ?? 0),
            }),
            { gross: 0, refunds: 0, disputes: 0 },
        );
    }, [selectedService, revenues, revenuesPerService]);

    const [customFrom, setCustomFrom] = React.useState<Dayjs | null>(null);
    const [customTo, setCustomTo] = React.useState<Dayjs | null>(null);

    async function fetchDashboardData(data: DashboardDateRange) {
        await getDashboardData({
            from: data.from,
            to: data.to,
        });
    }

    useEffect(() => {
        if (dashboardTab === 'customRange' && customFrom && customTo) {
            fetchDashboardData({
                from: customFrom.format('YYYY-MM-DD'),
                to: customTo.format('YYYY-MM-DD'),
            });
        } else if (dashboardTab !== 'customRange') {
            fetchDashboardData({
                from: getDashboardDateRange(dashboardTab).from,
                to: getDashboardDateRange(dashboardTab).to,
            });
        }
        // eslint-disable-next-line
    }, [tenant, dashboardTab, customFrom, customTo]);


    const config = useSelector(
        (state: RootState) => state.authentication.config,
    );
    // Every room-family tenant uses the same rental/create-object flow (matches
    // the sidebar's `useRentalSidebar` and `createObjectRouteFor`). When venue was
    // missing here the setup checklist's first step fell through to the station
    // variant ("Set up station" → station wizard) instead of "create your object" →
    // createAsset. Newly onboarded tenants are seeded with services: ['venue'].
    const isRental = roomUseCasesOf(config?.services).length > 0 && tenant !== 'demo';

    // Shown until the setup is actually finished — `useSetupProgress` answers
    // that, and already did: it was computed for the panel's own rows while the
    // panel's VISIBILITY was gated on booking volume in the selected period.
    // So the first booking hid a half-finished checklist, and changing the
    // period tab brought it back. One question, one source (CLAUDE.md).
    //
    // Still restricted to self-service tenants — its CTAs deep-link into the
    // Account page, which legacy/internal admins can't access — and still
    // hidden for masters: `isSelfServiceTenant` is slug-based and turns true
    // when a master views an onboarded tenant, but the checklist is the owner's
    // setup flow, not something a master should be nudged through.
    const isMaster = useIsMaster();
    const selfServiceTenant = isSelfServiceTenant(tenant);
    const { stepsComplete: dashboardStepsComplete, setupComplete } = useSetupProgress();
    const showSetupChecklist = selfServiceTenant && !setupComplete && !isMaster;

    // Subtitle lists the tenant's services that already have ≥1 asset
    // (e.g. "Venues · Guest apartments"). Filtering by asset existence
    // avoids confusing new users who see "Guest apartments" but haven't
    // configured any — only show categories they actually use.
    // Plural forms are preferred where natural (`<key>_plural`); falls
    // back to the singular for service types that read fine as-is.
    const subtitleNow = useMemo(() => new Date(), []);
    const { data: assetsForSubtitle } = useGetAssetsAvailabilityDataQuery({
        data: {
            year: String(subtitleNow.getFullYear()),
            month: String(subtitleNow.getMonth() + 1),
        },
    }, { skip: !(config?.services?.length) });
    const useCasesWithAssets = useMemo(
        () => new Set((assetsForSubtitle ?? []).flatMap((a) => a.useCases ?? [])),
        [assetsForSubtitle],
    );
    const serviceLabels = (config?.services ?? [])
        .filter((s) => useCasesWithAssets.has(s))
        .map((s) => {
            const plural = t(`service_type_selector.${s}_plural`, { defaultValue: '' });
            return plural || t(`service_type_selector.${s}`, { defaultValue: '' });
        })
        .filter(Boolean);
    const headerSubtitle = serviceLabels.join(' · ');

    // Create-booking options for this tenant (only services with an
    // implemented create flow). Multiple options render as a dropdown on
    // the quick-action tile — a tenant with e.g. apartments AND venues
    // must pick the service first instead of being forced into the first
    // service's flow. Same chooser as the bookings page header, including
    // its per-service asset filter (no "Venue booking" for a tenant whose
    // only assets are apartments); until the availability response
    // arrives, fall back to the unfiltered options so the tile stays
    // responsive.
    const createBookingOptions = useMemo(() => {
        const services = config?.services ?? [];
        const createOptions = config?.createBookingOptions ?? [];
        if (!assetsForSubtitle) {
            return getCreateBookingOptionsForTenant(services, createOptions);
        }
        return getCreateBookingOptionsForTenant(
            services.filter((s) => useCasesWithAssets.has(s)),
            createOptions.filter((s) => useCasesWithAssets.has(s)),
        );
    }, [config?.services, config?.createBookingOptions, assetsForSubtitle, useCasesWithAssets]);
    // Fallback for 0 options: the tenant's booking list (rental-style
    // tenants use /rental-bookings, station-style tenants /bookings —
    // same split as the "view all bookings" link below).
    const createBookingRoute = createBookingOptions.length === 1
        ? createBookingOptions[0].route
        : (isRental ? paths.rentalBooking : paths.bookings);

    // Quick-action gates: a restricted role (e.g. Service) must not be offered
    // create actions it lacks the permission for — the create cards are hidden
    // outright, not just disabled.
    const hasCreateBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.createBookings()),
    );
    const hasManageAssetsPermission = useSelector(
        makeHasPermissionSelector(Permission.createAssets()),
    );
    // Every use case has a create flow; which one depends on the tenant's
    // services. A tenant with no services at all has nothing to create yet.
    const tenantServices = config?.services ?? [];
    const canCreateObject = tenantServices.length > 0;
    const createObjectRoute = createObjectRouteFor(tenantServices);
    const createProblemReportRoute = isRental
        ? paths.rentalAddIssueReports
        : paths.addIssueReports;

    // Booking-create needs ≥1 asset to land somewhere useful; when the
    // tenant hasn't created one yet, the tile stays visible but greyed
    // and clicks fire an explainer snackbar.
    const hasAtLeastOneAsset = dashboardStepsComplete.createVenue;

    return (
        <div className={'px-4 sm:px-6 py-6 max-w-7xl'}>
            <PageHeader>
                <div className={'flex flex-col gap-0.5 grow'}>
                    <PageHeadline>
                        {t('dashboard.title')}
                    </PageHeadline>
                    {headerSubtitle && (
                        <span className={'text-sm text-foreground/60'}>{headerSubtitle}</span>
                    )}
                </div>
            </PageHeader>

            {/* Setup Checklist — pinned above the filter row so new tenants
                land on the next step immediately. `!isLoading` avoids the
                flicker where Redux's 0/0 initial state would briefly satisfy
                `!hasData` for tenants that actually have data. */}
            {showSetupChecklist && !isLoading && (
                <div className={'mb-6'}>
                    <SetupChecklist
                        isRental={isRental}
                        navigate={navigate}
                    />
                </div>
            )}

            {/* Period + service filters */}
            <div className={'flex flex-wrap items-center gap-3 mb-6 sm:mb-8'}>
                <div
                    className={cn(
                        'flex gap-2 bg-[#F3F3F3] rounded-full p-1 max-w-full',
                        'overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent whitespace-nowrap',
                        'scroll-smooth',
                    )}
                >
                    {TABS.map((tab) => {
                        const isActive = dashboardTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                data-active={isActive ? 'true' : 'false'}
                                className={cn(
                                    'px-4 sm:px-6 py-2 rounded-full font-medium text-sm',
                                    'transition-colors duration-150 flex-shrink-0',
                                    'focus:outline-none focus:ring-2 focus:ring-offset-2',
                                    isActive
                                        ? 'bg-primary text-text-buttonContrast shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700',
                                )}
                                onClick={() => dispatch(setDashboardTab(tab.key))}
                            >
                                {tab.label}
                            </button>
                        );
                    })}

                    {dashboardTab === 'customRange' && (
                        <DateRangePicker
                            from={customFrom}
                            to={customTo}
                            onChange={(from, to) => {
                                setCustomFrom(from);
                                setCustomTo(to);
                            }}
                            minDate={dayjs('2024-01-01')}
                            maxDate={dayjs()}
                        />
                    )}
                </div>

                {/* Service selector — global filter for all KPIs. Hidden when only
                    one (or zero) service has utilization data, since there's
                    nothing to switch between. */}
                {serviceTypes.length > 1 && (
                    <Select.Root value={selectedService} onValueChange={setSelectedService}>
                        <Select.Trigger
                            aria-label={t('dashboard.filter_by_service')}
                            className={cn(
                                'group inline-flex items-center justify-between gap-2 min-w-[180px]',
                                'rounded-full bg-white px-4 py-2 text-sm font-medium text-foreground',
                                'border border-foreground/15 shadow-sm',
                                'transition-all duration-150 outline-none cursor-pointer',
                                'hover:border-foreground/30 hover:shadow',
                                'focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30',
                                'data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/20',
                            )}
                        >
                            <Select.Value />
                            <Select.Icon>
                                <ChevronDown
                                    className={cn(
                                        'h-4 w-4 shrink-0 text-foreground/60 transition-transform duration-200',
                                        'group-data-[state=open]:rotate-180 group-data-[state=open]:text-primary',
                                    )}
                                />
                            </Select.Icon>
                        </Select.Trigger>
                        <Select.Portal>
                            <Select.Content
                                position={'popper'}
                                sideOffset={6}
                                className={cn(
                                    'z-[9999] min-w-[var(--radix-select-trigger-width)] overflow-hidden',
                                    'rounded-xl border border-foreground/10 bg-white shadow-xl',
                                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                                    'data-[side=bottom]:slide-in-from-top-1',
                                )}
                            >
                                <Select.Viewport className={'p-1.5 max-h-72'}>
                                    <Select.Item
                                        value={'all'}
                                        className={cn(
                                            'relative flex items-center justify-between gap-3 px-3 py-2 rounded-lg',
                                            'text-sm text-foreground cursor-pointer outline-none select-none',
                                            'data-[highlighted]:bg-primary/10',
                                            'data-[state=checked]:font-semibold data-[state=checked]:text-primary',
                                        )}
                                    >
                                        <Select.ItemText>{t('dashboard.all_services')}</Select.ItemText>
                                        <Select.ItemIndicator>
                                            <Check className={'h-4 w-4 text-primary'} />
                                        </Select.ItemIndicator>
                                    </Select.Item>
                                    {serviceTypes.length > 0 && (
                                        <Select.Separator className={'my-1 h-px bg-foreground/10'} />
                                    )}
                                    {serviceTypes.map((s) => (
                                        <Select.Item
                                            key={s}
                                            value={s}
                                            className={cn(
                                                'relative flex items-center justify-between gap-3 px-3 py-2 rounded-lg',
                                                'text-sm text-foreground cursor-pointer outline-none select-none',
                                                'data-[highlighted]:bg-primary/10',
                                                'data-[state=checked]:font-semibold data-[state=checked]:text-primary',
                                            )}
                                        >
                                            <Select.ItemText>
                                                {t(`service_type_selector.${s}`, { defaultValue: s })}
                                            </Select.ItemText>
                                            <Select.ItemIndicator>
                                                <Check className={'h-4 w-4 text-primary'} />
                                            </Select.ItemIndicator>
                                        </Select.Item>
                                    ))}
                                </Select.Viewport>
                            </Select.Content>
                        </Select.Portal>
                    </Select.Root>
                )}
            </div>

            {/* Content */}
            {isLoading ? (
                <div className={'flex w-full h-[60vh] m-auto'}>
                    <LoadingPage />
                </div>
            ) : (
                <div className={'flex flex-col gap-6'}>
                    {/* KPI Cards */}
                    <div>
                        <h2 className={'text-base font-semibold text-foreground mb-3'}>
                            {t('dashboard.overview')}
                        </h2>
                        <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
                            {/* Bookings Card — perService since #895, so a single-service
                                filter aggregates the matching groups. Trend chip is hidden
                                under filter to avoid misleading mixed-trend approximations. */}
                            <KpiCard
                                label={t('dashboard.total_bookings')}
                                value={selectedService === 'all'
                                    ? String(bookings?.current)
                                    : String(filteredBookings ?? 0)}
                                trend={selectedService === 'all' ? bookings?.trend : undefined}
                                percentageChange={selectedService === 'all' ? bookings?.percentageChange : undefined}
                                failed={failedMetrics.has('bookings')}
                                icon={<BookingsFill className={'w-6 h-6 text-primary'} />}
                                iconBg={'bg-primary/10'}
                                tooltip={t('dashboard.total_bookings_desc')}
                            />

                            {/* Revenue Card — same per-service aggregation as bookings (#895).
                                The headline is NET since #2028; gross, refunds and disputes
                                sit underneath as their own numbers rather than being folded
                                into it. */}
                            {tenant !== 'segmenta_v2' && (
                                <KpiCard
                                    label={t('dashboard.total_revenue')}
                                    value={selectedService === 'all'
                                        ? formatSignedCurrency(revenues?.current, lang)
                                        : formatSignedCurrency(filteredRevenues ?? 0, lang)}
                                    trend={selectedService === 'all' ? revenues?.trend : undefined}
                                    percentageChange={selectedService === 'all' ? revenues?.percentageChange : undefined}
                                    failed={failedMetrics.has('revenues')}
                                    icon={<Euro className={'w-6 h-6 text-primary'} />}
                                    iconBg={'bg-primary/10'}
                                    tooltip={t('dashboard.total_revenue_desc')}
                                    breakdown={revenueBreakdown ? [
                                        {
                                            label: t('dashboard.revenue_gross'),
                                            value: formatSignedCurrency(revenueBreakdown.gross, lang),
                                        },
                                        {
                                            label: t('dashboard.revenue_refunded'),
                                            value: formatSignedCurrency(revenueBreakdown.refunds, lang),
                                        },
                                        {
                                            label: t('dashboard.revenue_disputed'),
                                            value: formatSignedCurrency(revenueBreakdown.disputes, lang),
                                        },
                                    ] : undefined}
                                />
                            )}

                            {/* Top-line: Umsatzauslastung (capture rate, #903). Per-service
                                view (#903): relabel to time-based "Auslastung" — the filtered
                                value is bookedHours / bookableHours, not capture rate. Trend
                                is omitted under filter because per-service previous-period
                                booked hours aren't on the type. */}
                            <KpiCard
                                label={selectedService === 'all'
                                    ? t('dashboard.revenue_capture_rate')
                                    : t('dashboard.utilization')}
                                value={selectedService === 'all'
                                    ? (revenueCaptureRate
                                        ? `${Number(revenueCaptureRate.current).toFixed(1)}%`
                                        : t('common.not_applicable'))
                                    : (filteredCaptureRate === null
                                        ? t('common.not_applicable')
                                        : `${filteredCaptureRate.toFixed(1)}%`)}
                                trend={selectedService === 'all' ? revenueCaptureRate?.trend : undefined}
                                percentageChange={selectedService === 'all' ? revenueCaptureRate?.percentageChange : undefined}
                                icon={<BarChart3 className={'w-6 h-6 text-primary'} />}
                                iconBg={'bg-primary/10'}
                                tooltip={selectedService === 'all'
                                    ? t('dashboard.revenue_capture_rate_desc')
                                    : t('dashboard.utilization_desc')}
                                notAvailable={selectedService === 'all'
                                    ? !revenueCaptureRate
                                    : filteredCaptureRate === null}
                                failed={failedMetrics.has('utilization')}
                                breakdown={contractedHoursShown > 0 ? [{
                                    label: t('dashboard.contracted_hours'),
                                    value: t('dashboard.hours_value', {
                                        hours: Math.round(contractedHoursShown),
                                    }),
                                }] : undefined}
                            />

                            {/* M8 approval (#1809): a count of open decisions, not a
                                period figure — so no trend and no service filter. Links
                                to the bookings list narrowed to exactly these rows. */}
                            <AwaitingApprovalKpi services={config?.services} count={awaitingApproval} />
                        </div>
                    </div>

                    {/* Today and tomorrow (#3016): current-state counts, not period
                        figures — so no trend and no service filter. Each figure links
                        to the bookings list narrowed to exactly its rows. */}
                    <DaySummarySection
                        services={config?.services}
                        isRental={isRental}
                    />

                    {/* Quick Actions */}
                    <div>
                        <h2 className={'text-base font-semibold text-foreground mb-3'}>
                            {t('dashboard.quick_actions')}
                        </h2>
                        <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'}>
                            {hasCreateBookingsPermission && (
                                hasAtLeastOneAsset && createBookingOptions.length > 1 ? (
                                    <DropdownMenu
                                        fullWidth={true}
                                        trigger={
                                            <QuickActionCard
                                                title={t('dashboard.action_create_booking')}
                                                description={t('dashboard.action_create_booking_desc')}
                                                icon={<Plus className={'w-5 h-5'} />}
                                            />
                                        }
                                        items={createBookingOptions.map((option) => ({
                                            id: option.id,
                                            label: t(option.translationKey),
                                            onClick: () => navigate(option.route),
                                        }))}
                                    />
                                ) : (
                                    <QuickActionCard
                                        title={t('dashboard.action_create_booking')}
                                        description={t('dashboard.action_create_booking_desc')}
                                        icon={<Plus className={'w-5 h-5'} />}
                                        onClick={hasAtLeastOneAsset
                                            ? () => navigate(createBookingRoute)
                                            : () => enqueueWarningSnackbar(
                                                t('bookings.bookings_table.no_asset_for_booking'),
                                            )}
                                        disabled={!hasAtLeastOneAsset}
                                    />
                                )
                            )}
                            {hasManageAssetsPermission && (
                                <QuickActionCard
                                    title={t('dashboard.action_create_object')}
                                    description={t('dashboard.action_create_object_desc')}
                                    icon={<Building2 className={'w-5 h-5'} />}
                                    onClick={canCreateObject ? () => navigate(createObjectRoute) : undefined}
                                    disabled={!canCreateObject}
                                />
                            )}
                            <QuickActionCard
                                title={t('dashboard.action_create_problem_report')}
                                description={t('dashboard.action_create_problem_report_desc')}
                                icon={<AlertTriangle className={'w-5 h-5'} />}
                                onClick={() => navigate(createProblemReportRoute)}
                            />
                        </div>
                    </div>

                    {/* Latest Activity — two columns */}
                    <div>
                        <h2 className={'text-base font-semibold text-foreground mb-3'}>
                            {t('dashboard.latest_activity')}
                        </h2>
                        <div className={'grid grid-cols-1 lg:grid-cols-2 gap-4'}>
                            {/* Latest Bookings (#878) */}
                            <RecentActivityCard
                                title={t('dashboard.latest_bookings')}
                                emptyText={t(activityEmptyKey(dashboardDataFailed, 'dashboard.no_bookings_to_show'))}
                                items={safeRecentBookings.map((b) => mapRecentBookingToItem(b, t, lang, !!isRental))}
                                viewAllLabel={t('dashboard.view_all_bookings')}
                                onViewAll={() => navigate(isRental ? paths.rentalBooking : paths.bookings)}
                            />

                            {/* Latest Problem Reports (#878) */}
                            <RecentActivityCard
                                title={t('dashboard.latest_problem_reports')}
                                emptyText={t(activityEmptyKey(dashboardDataFailed, 'dashboard.no_problem_reports_to_show'))}
                                items={safeRecentIssueReports.map((r) => mapRecentIssueReportToItem(r, t, lang, !!isRental))}
                                viewAllLabel={t('dashboard.view_all_issue_reports')}
                                onViewAll={() => navigate(isRental ? paths.rentalIssueReports : paths.issueReports)}
                            />
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
}

/* --- Sub-components --- */

/**
 * M8 approval (#1809): every tenant with a room use case can have
 * reservations waiting for a decision. Absent from an older backend's
 * response, so the tile only shows once the count actually arrives.
 */
function AwaitingApprovalKpi({ services, count }: Readonly<{ services?: string[], count?: number }>) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    if (roomUseCasesOf(services).length === 0 || typeof count !== 'number') return null;
    return (
        <KpiCard
            label={t('dashboard.awaiting_approval')}
            value={String(count)}
            icon={<Hourglass className={'w-6 h-6 text-primary'} />}
            iconBg={'bg-primary/10'}
            tooltip={t('dashboard.awaiting_approval_desc')}
            action={{
                label: t('dashboard.awaiting_approval_action'),
                onClick: () => navigate(makeBookingsAwaitingApprovalUrl()),
            }}
        />
    );
}

interface KpiCardProps {
    label: string;
    value: string;
    icon: React.ReactNode;
    iconBg: string;
    tooltip?: string;
    // When omitted (e.g. value is a fallback like "n/a"), the trend chip is hidden.
    trend?: 'up' | 'down' | 'neutral';
    percentageChange?: number;
    // Renders the value italic + faded for fallback states ("n/a" / "missing").
    notAvailable?: boolean;
    // The figure could not be loaded (#2864). Replaces value, trend and
    // breakdown with a message that cannot be mistaken for a zero or for n/a.
    failed?: boolean;
    // Optional secondary figures the headline is composed of — e.g. revenue's
    // gross / refunded / disputed (#2028). Rendered as plain label-value rows
    // under the value, never blended into it. Omit to render nothing.
    breakdown?: Array<{ label: string, value: string }>;
    // Optional plain-text link under the value, to the list behind the figure.
    action?: { label: string, onClick: () => void };
}

function KpiCard({
    label, value, trend, percentageChange, icon, iconBg, tooltip, notAvailable, failed, breakdown, action,
}: Readonly<KpiCardProps>) {
    const { t } = useTranslation();
    const showFigure = !failed;
    const showTrend = showFigure && !notAvailable && trend !== undefined && percentageChange !== undefined;
    return (
        <div className={'bg-white rounded-2xl p-5 sm:p-6 flex flex-col shadow-sm'}>
            <div className={'flex items-center justify-between mb-3'}>
                <div className={'flex items-center gap-1.5'}>
                    <span className={'text-sm text-foreground/60 font-medium'}>{label}</span>
                    {tooltip && (
                        <Tooltip title={tooltip} arrow placement={'top'}>
                            <span className={'inline-flex'}>
                                <Info className={'w-3.5 h-3.5 text-foreground/40 cursor-help'} />
                            </span>
                        </Tooltip>
                    )}
                </div>
                <span className={cn('p-2 rounded-xl', iconBg)}>
                    {icon}
                </span>
            </div>
            {failed ? (
                <span role={'alert'} className={'flex items-center gap-2 text-sm font-medium text-foreground/70'}>
                    <AlertTriangle className={'w-4 h-4 shrink-0'} />
                    {t('dashboard.figure_failed')}
                </span>
            ) : (
                <span className={cn(
                    'text-2xl sm:text-3xl font-bold',
                    notAvailable ? 'italic font-normal text-foreground/40' : 'text-foreground',
                )}
                >
                    {value}
                </span>
            )}
            {showFigure && !notAvailable && breakdown && breakdown.length > 0 && (
                <dl className={'mt-3 flex flex-col gap-1'}>
                    {breakdown.map((entry) => (
                        <div key={entry.label} className={'flex items-center justify-between gap-2'}>
                            <dt className={'text-xs text-foreground/60'}>{entry.label}</dt>
                            <dd className={'text-xs font-medium text-foreground/80 tabular-nums'}>
                                {entry.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
            {showTrend && (
                <div className={'flex items-center gap-1.5 mt-2 flex-wrap'}>
                    {trend === 'up' && <TrendingUp className={'w-4 h-4 text-emerald-600'} />}
                    {trend === 'down' && <TrendingDown className={'w-4 h-4 text-red-500'} />}
                    <span
                        className={cn(
                            'text-xs font-medium',
                            trend === 'up' && 'text-emerald-600',
                            trend === 'down' && 'text-red-500',
                            trend === 'neutral' && 'text-foreground/40',
                        )}
                    >
                        {trend === 'up' ? '+' : trend === 'down' ? '' : ''}{percentageChange}%
                    </span>
                    <span className={'text-xs text-foreground/40'}>
                        {t('dashboard.vs_previous_period')}
                    </span>
                </div>
            )}
            {action && (
                <button
                    type={'button'}
                    onClick={action.onClick}
                    className={'mt-3 self-start text-sm font-medium text-primary hover:underline'}
                >
                    {action.label}
                </button>
            )}
        </div>
    );
}

interface QuickActionCardProps {
    title: string;
    description: string;
    icon: React.ReactNode;
    /** Always fires on click, even when `disabled` — lets the caller
     *  show a snackbar explaining why a disabled tile can't be used. */
    onClick?: () => void;
    /** Renders the tile in a muted "not actionable" visual state. */
    disabled?: boolean;
    /** Optional small pill rendered below the description when disabled.
     *  Used for "Coming soon" tiles. Omit for disabled-due-to-missing-
     *  prerequisite tiles where the snackbar carries the message. */
    disabledBadge?: string;
}

function QuickActionCard({ title, description, icon, onClick, disabled, disabledBadge }: Readonly<QuickActionCardProps>) {
    return (
        <button
            onClick={onClick}
            aria-disabled={disabled}
            className={cn(
                'bg-white rounded-xl p-4 flex items-start gap-3 text-left shadow-sm',
                'border border-transparent transition-all group relative w-full h-full',
                disabled
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:border-primary/30 cursor-pointer',
            )}
        >
            <div className={cn(
                'rounded-lg p-2 shrink-0 transition-colors',
                disabled
                    ? 'bg-foreground/5 text-foreground/40'
                    : 'bg-primary/10 text-primary group-hover:bg-primary/20',
            )}
            >
                {icon}
            </div>
            <div className={'flex flex-col min-w-0 flex-1'}>
                <span className={cn(
                    'text-sm font-semibold transition-colors',
                    disabled ? 'text-foreground/50' : 'text-foreground group-hover:text-primary',
                )}
                >
                    {title}
                </span>
                <span className={cn(
                    'text-xs mt-0.5',
                    disabled ? 'text-foreground/40' : 'text-foreground/50',
                )}
                >
                    {description}
                </span>
                {disabled && disabledBadge && (
                    <span className={cn(
                        'mt-2 inline-flex items-center self-start',
                        'px-2 py-0.5 rounded-full bg-foreground/5',
                        'text-xs font-medium text-foreground/50',
                    )}
                    >
                        {disabledBadge}
                    </span>
                )}
            </div>
            {!disabled && (
                <ArrowRight className={'w-4 h-4 text-foreground/20 shrink-0 mt-0.5 group-hover:text-primary transition-colors'} />
            )}
        </button>
    );
}

export interface RecentActivityItem {
    id: string;
    label: string;
    sublabel: string;
    status: string;
    statusColor: string;
    date: string;
    to: string;
}

// Booking status → Tailwind pill classes. Mirrors the table in
// pages/assets/components/RecentBookingsSection.tsx so the dashboard pills
// look the same as the asset-detail recent-bookings pills. Status values
// come from RentalPGService.applyBookingStatusOverrides; unknown values
// fall through to the gray fallback rather than crashing.
const BOOKING_STATUS_COLORS: Record<string, string> = {
    booked: 'bg-green-100 text-green-800',
    active: 'bg-blue-100 text-blue-800',
    finished: 'bg-gray-100 text-gray-800',
    canceled: 'bg-red-100 text-red-800',
    reserved: 'bg-purple-100 text-purple-800',
    incompleted: 'bg-amber-100 text-amber-800',
    blocked: 'bg-zinc-100 text-zinc-800',
    conflicted: 'bg-orange-100 text-orange-800',
    overtime: 'bg-rose-100 text-rose-800',
};

const ISSUE_STATE_COLORS: Record<string, string> = {
    open: 'bg-red-100 text-red-800',
    in_progress: 'bg-blue-100 text-blue-800',
    on_hold: 'bg-amber-100 text-amber-800',
    resolved: 'bg-green-100 text-green-800',
};

const formatRecentDate = (iso: string, lang: string): string => {
    try {
        const locale = lang.startsWith('de') ? 'de-DE' : 'en-US';
        return new Date(iso).toLocaleDateString(locale, {
            timeZone: STATION_TIME_ZONE,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    } catch {
        return iso;
    }
};

function mapRecentBookingToItem(
    b: RecentBookingItem,
    t: (key: string, opts?: Record<string, unknown>) => string,
    lang: string,
    isRental: boolean,
): RecentActivityItem {
    const statusKey = b.status.toLowerCase();
    return {
        id: b.id,
        label: b.bookingNumber,
        sublabel: b.customerName ?? b.assetName ?? '',
        status: t(`dashboard.assets_availability.status.${statusKey}`, { defaultValue: b.status }),
        statusColor: BOOKING_STATUS_COLORS[statusKey] ?? 'bg-gray-100 text-gray-800',
        date: formatRecentDate(b.createdAt, lang),
        // Always the rental URL, for every use case. BookingDetailsPage is
        // registered ONLY at paths.rentalBookingDetails and is the dispatcher
        // for all of them — it inspects `useCase` and renders the apartment,
        // venue, room or rental variant itself. paths.bookingDetails
        // (/bookings/:bookingId) is legacy and has no <Route> in App.tsx, so
        // sending a non-rental tenant there matched nothing and rendered a
        // blank screen. Same reasoning as MasterBookingsPage's row click.
        to: makeRentalBookingDetailsUrl(b.id),
    };
}

function mapRecentIssueReportToItem(
    r: RecentIssueReportItem,
    t: (key: string, opts?: Record<string, unknown>) => string,
    lang: string,
    isRental: boolean,
): RecentActivityItem {
    const stateKey = r.state.toLowerCase();
    // Compose a meaningful label since damage reports don't have a "name":
    // booking number first (when present), else issue type, else a generic fallback.
    const label = r.bookingNumber || r.issueType || t('issue_reports.issue_report');
    return {
        id: r.id,
        label,
        sublabel: r.assetName ?? '',
        status: t(`issue_reports.card.${stateKey}`, { defaultValue: r.state }),
        statusColor: ISSUE_STATE_COLORS[stateKey] ?? 'bg-gray-100 text-gray-800',
        date: formatRecentDate(r.createdAt, lang),
        to: isRental ? makeRentalIssueReportDetailsUrl(r.id) : makeIssueReportDetailsUrl(r.id),
    };
}

interface RecentActivityCardProps {
    title: string;
    emptyText: string;
    items: RecentActivityItem[];
    viewAllLabel: string;
    onViewAll: () => void;
}

function RecentActivityCard({ title, emptyText, items, viewAllLabel, onViewAll }: RecentActivityCardProps) {
    return (
        <div className={'bg-white rounded-2xl shadow-sm flex flex-col'}>
            <div className={'flex items-center justify-between px-5 pt-5 pb-3'}>
                <h3 className={'text-sm font-semibold text-foreground'}>{title}</h3>
                {items.length > 0 && (
                    <button
                        onClick={onViewAll}
                        className={'text-xs text-primary hover:underline font-medium'}
                    >
                        {viewAllLabel}
                    </button>
                )}
            </div>
            {items.length === 0 ? (
                <div className={'px-5 pb-5'}>
                    <p className={'text-sm text-foreground/40 italic'}>{emptyText}</p>
                </div>
            ) : (
                <div className={'flex flex-col'}>
                    {items.map((item) => (
                        <Link
                            key={item.id}
                            to={item.to}
                            className={'flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors border-t border-foreground/5'}
                        >
                            <div className={'flex flex-col min-w-0 flex-1'}>
                                <span className={'text-sm font-medium text-foreground truncate'}>{item.label}</span>
                                <span className={'text-xs text-foreground/50 truncate'}>{item.sublabel}</span>
                            </div>
                            <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium shrink-0', item.statusColor)}>
                                {item.status}
                            </span>
                            <span className={'text-xs text-foreground/40 shrink-0'}>{item.date}</span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

/* --- Setup Checklist --- */

interface SetupStep {
    key: string;
    icon: React.ReactNode;
    titleKey: string;
    descKey: string;
    ctaKey: string;
    done: boolean;
    onClick: () => void;
    /** Step is visible in the checklist for context but not yet shippable
     *  end-to-end. Renders a "Coming soon" pill instead of a CTA, never
     *  becomes the "current" step, and isn't counted toward "incomplete"
     *  for the active-step calculation. */
    comingSoon?: boolean;
    /** Step can be left open without blocking the steps after it (bank
     *  details for a tenant that is not paid by bank transfer). Never becomes
     *  the "current" step, but keeps its button while it is reachable. */
    optional?: boolean;
}

/** The current step always has its button; an open optional step keeps its
 *  own while it is reachable, since it never becomes "current". */
const stepHasButton = (step: SetupStep, isCurrent: boolean, isLocked: boolean): boolean =>
    isCurrent || (!!step.optional && !step.done && !isLocked);

interface SetupChecklistProps {
    isRental: boolean | undefined;
    navigate: (path: string) => void;
}

function SetupChecklist({ isRental, navigate }: SetupChecklistProps) {
    const { t } = useTranslation();
    // Live completion state. `createVenue` is wired to the asset count
    // today; the other three remain TODOs in the hook (see
    // useSetupProgress.ts) until the matching backend fields surface.
    const { stepsComplete, bankDetailsRequired, assetsFailed } = useSetupProgress();
    // The onboarding import is the account's page: owner, or master viewing the tenant.
    const { canAccess: canSetUpByFile } = useCanAccessAccount();

    const steps: SetupStep[] = [
        {
            key: 'createVenue',
            icon: <MapPin className={'w-5 h-5'} />,
            titleKey: isRental ? 'dashboard.setup_step_venue_title_rental' : 'dashboard.setup_step_venue_title',
            descKey: isRental ? 'dashboard.setup_step_venue_desc_rental' : 'dashboard.setup_step_venue_desc',
            ctaKey: isRental ? 'dashboard.setup_step_venue_cta_rental' : 'dashboard.setup_step_venue_cta',
            done: stepsComplete.createVenue,
            // Station tenants create a station, not open access management:
            // `/depots` is the lock/hardware view and, without the add-on,
            // an upsell page.
            onClick: () => navigate(isRental ? paths.createAsset : paths.createStation),
        },
        {
            // #1956: its own step. Public contact used to be written together
            // with the bank object, so the bank step covered it; splitting the
            // endpoints left it skippable while every room/venue customer
            // email still renders it.
            key: 'contactInfo',
            icon: <Mail className={'w-5 h-5'} />,
            titleKey: 'dashboard.setup_step_contact_title',
            descKey: 'dashboard.setup_step_contact_desc',
            ctaKey: 'dashboard.setup_step_contact_cta',
            done: stepsComplete.contactInfo,
            onClick: () => navigate(`${paths.account}#contact`),
        },
        {
            key: 'legalNotice',
            icon: <Scale className={'w-5 h-5'} />,
            titleKey: 'dashboard.setup_step_legal_title',
            descKey: 'dashboard.setup_step_legal_desc',
            ctaKey: 'dashboard.setup_step_legal_cta',
            done: stepsComplete.legalNotice,
            onClick: () => navigate(`${paths.account}#legal-notice`),
        },
        {
            // The step the checklist never had, though every step around it
            // depends on its answers: whether the offer costs anything, how a
            // reservation is settled — and therefore whether the bank step
            // below applies at all. That is why it sits HERE and not at the
            // end: ahead of the bank details it decides, behind the identity
            // data the tenant fills in once.
            key: 'servicesConfigured',
            icon: <SlidersHorizontal className={'w-5 h-5'} />,
            titleKey: 'dashboard.setup_step_services_title',
            descKey: 'dashboard.setup_step_services_desc',
            ctaKey: 'dashboard.setup_step_services_cta',
            done: stepsComplete.servicesConfigured,
            // The anchor selects the Use-Cases tab (`TAB_BY_ANCHOR` in
            // ServicesPage) before scrolling — a section on an unselected tab
            // scrolls to nothing.
            onClick: () => navigate(`${paths.rentalProducts}#service-configuration`),
        },
        {
            // Only a real step when a payment request kolula sends carries the
            // bank details: a bank transfer (the reservation mail), or an
            // invoice kolula issues (PO, 2026-10-19). Two kinds of tenant need
            // no IBAN and must not be told otherwise: a free offer needs no
            // payment route at all (ADR-0016 Amendment 4 ends the configuration
            // at `charging: 'free'`), and a tenant whose own system issues the
            // invoices sends its own payment request. Either way an IBAN must
            // not stand between them and a live booking page, nor keep this
            // checklist open forever.
            key: 'bankDetails',
            icon: <CreditCard className={'w-5 h-5'} />,
            titleKey: bankDetailsRequired
                ? 'dashboard.setup_step_bank_title'
                : 'dashboard.setup_step_bank_title_optional',
            descKey: bankDetailsRequired
                ? 'dashboard.setup_step_bank_desc'
                : 'dashboard.setup_step_bank_desc_optional',
            ctaKey: 'dashboard.setup_step_bank_cta',
            done: stepsComplete.bankDetails,
            // Optional unless a payment request needs it: an open optional step must
            // not keep the web-app step locked behind it.
            optional: !bankDetailsRequired,
            onClick: () => navigate(`${paths.account}#bank-details`),
        },
        {
            key: 'bookingPage',
            icon: <Globe className={'w-5 h-5'} />,
            titleKey: 'dashboard.setup_step_booking_title',
            descKey: 'dashboard.setup_step_booking_desc',
            ctaKey: 'dashboard.setup_step_booking_cta',
            done: stepsComplete.bookingPage,
            // Services, not Account: the activation moved next to the
            // check-in/check-out flow on 2026-09-12 (ADR-0005 amendment). The
            // Services page maps this anchor to its usage-flow tab, so the deep
            // link still selects the right tab and scrolls — an anchor whose
            // tab is not selected scrolls to nothing.
            onClick: () => navigate(`${paths.rentalProducts}#booking-page`),
        },
    ];

    // The object read failed: whether the first step is done is not known, and
    // every step after it is placed by it. Say so instead of guessing "no object".
    if (assetsFailed) {
        return (
            <div className={'bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-6 sm:p-8'}>
                <h2 className={'text-lg font-semibold text-foreground'}>{t('dashboard.getting_started_title')}</h2>
                <p role={'alert'} className={'text-sm text-foreground mt-2'}>{t('dashboard.setup_progress_failed')}</p>
            </div>
        );
    }

    const completedCount = steps.filter((s) => s.done).length;
    // Only actionable steps participate in the "what's next" calculation;
    // a comingSoon step would otherwise become "current" once everything
    // before it is done, which is wrong while the feature isn't shippable.
    const firstIncompleteIndex = steps.findIndex((s) => !s.done && !s.comingSoon && !s.optional);

    return (
        <div className={'bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-6 sm:p-8'}>
            <div className={'flex items-start gap-4 mb-6'}>
                <div className={'flex flex-col gap-1 flex-1 min-w-0'}>
                    <h2 className={'text-lg font-semibold text-foreground'}>
                        {t('dashboard.getting_started_title')}
                    </h2>
                    <p className={'text-sm text-foreground/60'}>
                        {t('dashboard.setup_progress', { done: completedCount, total: steps.length })}
                    </p>
                    {/* Progress bar */}
                    <div className={'w-48 h-1.5 bg-black/5 rounded-full mt-1 overflow-hidden'}>
                        <div
                            className={'h-full bg-primary rounded-full transition-all duration-500'}
                            style={{ width: `${(completedCount / steps.length) * 100}%` }}
                        />
                    </div>
                    {/* The onboarding import fills most steps at once (plan onboarding-import.md). */}
                    {canSetUpByFile && (
                        <p className={'text-sm text-foreground/60 mt-2'}>
                            {t('onboarding_import.dashboard_hint')}{' '}
                            <button
                                type={'button'}
                                className={'font-medium text-primary hover:underline'}
                                onClick={() => navigate(paths.onboardingImport)}
                            >
                                {t('onboarding_import.open')}
                            </button>
                        </p>
                    )}
                </div>
                <div className={'bg-primary/10 rounded-xl p-3 shrink-0'}>
                    <Rocket className={'w-6 h-6 text-primary'} />
                </div>
            </div>

            <div className={'flex flex-col gap-3'}>
                {steps.map((step, index) => {
                    const isCurrent = index === firstIncompleteIndex;
                    const isLocked =
                        !step.comingSoon
                        && index > firstIncompleteIndex
                        && firstIncompleteIndex >= 0;
                    // Coming-soon steps share the locked treatment —
                    // the "Bald verfügbar" pill is the only thing that
                    // sets them apart visually. Otherwise the white-ish
                    // background read as "actionable like step 1".
                    const isMuted = isLocked || step.comingSoon;

                    return (
                        <div
                            key={step.key}
                            className={cn(
                                'flex items-start gap-4 rounded-xl p-4 transition-all',
                                step.done && 'bg-white/60',
                                isCurrent && 'bg-white shadow-sm',
                                isMuted && 'bg-white/30 opacity-60',
                            )}
                        >
                            {/* Step indicator */}
                            <div className={cn(
                                'w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold',
                                step.done && 'bg-emerald-100 text-emerald-600',
                                isCurrent && 'bg-primary text-text-buttonContrast',
                                isMuted && 'bg-gray-100 text-gray-400',
                            )}
                            >
                                {step.done ? <Check className={'w-4 h-4'} /> : index + 1}
                            </div>

                            {/* Content */}
                            <div className={'flex flex-col gap-1 min-w-0 flex-1'}>
                                <div className={'flex items-center gap-2 flex-wrap'}>
                                    <span className={cn(
                                        'text-sm font-semibold',
                                        step.done && 'text-foreground/50 line-through',
                                        isCurrent && 'text-foreground',
                                        isMuted && 'text-foreground/40',
                                    )}
                                    >
                                        {t(step.titleKey)}
                                    </span>
                                    {step.comingSoon && (
                                        <span
                                            className={cn(
                                                'inline-flex items-center px-1.5 py-0.5 rounded-full',
                                                'text-[10px] font-medium text-amber-800 bg-amber-100',
                                            )}
                                        >
                                            {t('dashboard.setup_step_coming_soon')}
                                        </span>
                                    )}
                                </div>
                                <p className={cn(
                                    'text-xs',
                                    step.done ? 'text-foreground/30' : 'text-foreground/50',
                                )}
                                >
                                    {t(step.descKey)}
                                </p>
                                {stepHasButton(step, isCurrent, isLocked) && (
                                    <button
                                        className={cn(
                                            'mt-2 px-3 py-1.5',
                                            'bg-primary text-text-buttonContrast rounded-lg',
                                            'text-xs font-medium hover:opacity-90 transition-opacity w-fit',
                                        )}
                                        onClick={step.onClick}
                                    >
                                        {t(step.ctaKey)}
                                    </button>
                                )}
                            </div>

                            {/* Icon */}
                            <div className={cn(
                                'rounded-lg p-2 shrink-0',
                                step.done ? 'text-foreground/20' : isCurrent ? 'text-primary/60' : 'text-foreground/20',
                            )}
                            >
                                {step.icon}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
